import React, { useCallback, useState } from 'react'
import cn from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import IconButton from 'components/Luxkit/Button/IconButton'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import ReactModal from 'react-modal'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LightBoxContext from './LightBoxContext'
import { MODAL_TRANSITION_DURATION } from './modalConstants'
import { DrawerSlideDownAnimation, DrawerSlideUpAnimation } from './ModalSheet'

const LightboxOverlay = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: end;
  backdrop-filter: blur(2px);
  max-height: calc(var(--vh, 1vh) * 100);
  overflow-y: unset !important; /* this is only here until the legacy modal is ditched */

  ${mediaQueryUp.sm} {
    align-items: center;
    justify-content: center;
    && {
      transition-property: opacity, background-color;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

const LightboxElement = styled(ReactModal)`
  position: relative;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.eight};
  border-radius: 0;
  box-shadow: ${props => props.theme.shadow.top.medium};
  min-height: ${rem(54)};
  height: 100%;
  width: 100%;

  animation-name: ${DrawerSlideDownAnimation};
  animation-iteration-count: 1;
  animation-duration: 300ms;
  animation-fill-mode: forwards;

  &.is-open {
    animation-name: ${DrawerSlideUpAnimation};
  }

  ${mediaQueryUp.sm} {
    animation: none;

    &.is-open {
      animation: none;
    }
  }
`

const CloseButton = styled(IconButton)`
  position: absolute;
  right: ${rem(16)};
  top: ${rem(16)};
  z-index: 1;
`

function LightBoxProvider(props: React.PropsWithChildren) {
  const [lightbox, setLightbox] = useState<{ resolve: any, element: React.ReactNode}>()
  const [open, setOpen] = useState<boolean>()

  const showLightbox = useCallback((lightbox: React.ReactNode) => {
    return new Promise<void>(resolve => {
      setLightbox({
        element: lightbox,
        resolve,
      })
      setOpen(true)
    })
  }, [])

  const handleClose = useCallback((event) => {
    event.stopPropagation()
    lightbox?.resolve()
    setOpen(false)
  }, [lightbox])

  return <>
    <LightboxElement
      className={cn({ 'is-open': open })}
      overlayElement={(props, content) => <LightboxOverlay {...props}>{content}</LightboxOverlay>}
      overlayClassName="luxkit-lightbox__overlay"
      closeTimeoutMS={MODAL_TRANSITION_DURATION}
      isOpen={open}
      shouldCloseOnEsc
      shouldReturnFocusAfterClose={false}
      onRequestClose={handleClose}
      onAfterClose={() => setLightbox(undefined)}
    >
      <CloseButton
        kind="tertiary"
        variant="ghost"
        outdent="right"
        onClick={handleClose}
      >
        <LineTimesIcon />
      </CloseButton>
      {lightbox?.element}
    </LightboxElement>
    <LightBoxContext.Provider value={showLightbox}>
      {props.children}
    </LightBoxContext.Provider>
  </>
}

export default LightBoxProvider
